import type { ProcessRunStatus, ProcessStatus, ConfigChangeSummary, ProvenanceEntryStatus, ProcessTriggerType, ProcessExecutionKind, OwnerScope, MessageDirection, ActiveProcessRunItem, XeroEventTriggerDefinition } from '@vivport/backend/types';
import type { ProcessActiveRun, ProcessExecutionListItem, ProcessLeaseSummary } from '@vivport/backend/types';
import type { MissionControlCancelRunResponse, ProcessRunRefKind } from '@vivport/backend/types/mission-control';
import { httpClient } from './http-client';

export interface ProcessListItem {
  id: string;
  name: string;
  description: string | null;
  status: ProcessStatus;
  executionKind: ProcessExecutionKind;
  handlerKey: string | null;
  triggerType: ProcessTriggerType;
  triggerDefinition: {
    tagId?: number;
    contentType?: string;
    cron?: string;
    timezone?: string;
    runAsMemberId?: number;
    matchCriteria?: string;
    defaultQuery?: string;
    defaultMaxResults?: number;
    includeSpamTrash?: boolean;
    directions?: MessageDirection[];
    defaultPastDays?: number;
    defaultFutureDays?: number;
    tagGroupId?: number;
    rootDocumentCode?: string;
    eventCategory?: XeroEventTriggerDefinition['eventCategory'];
    eventTypes?: XeroEventTriggerDefinition['eventTypes'];
    invoiceTypes?: XeroEventTriggerDefinition['invoiceTypes'];
    invoiceStatuses?: XeroEventTriggerDefinition['invoiceStatuses'];
  };
  agentId: number | null;
  agentName: string | null;
  agentOwnerScope: OwnerScope | null;
  systemAgentKey: string | null;
  systemAgentName: string | null;
  systemAgentDescription: string | null;
  skillIds: number[];
  inputTemplate: string | null;
  createdAt: string;
  updatedAt: string;
  lastRunAt: string | null;
  lease: ProcessLeaseSummary;
  activeRuns: ProcessActiveRun[];
  lastChange?: ConfigChangeSummary | null;
}

export interface ProcessesResponse {
  items: ProcessListItem[];
  processesEnabled: boolean;
  processesDisabledReason: string | null;
}

export interface ProcessRunListItem {
  id: number;
  code: string;
  processId: string;
  status: ProcessRunStatus;
  triggerType: ProcessTriggerType;
  triggerEvent: Record<string, unknown> | null;
  inputMessage: string;
  inputContext: Record<string, unknown> | null;
  error: string | null;
  attempts: number;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  agentRunId: string | null;
  lastEntrySequence: number;
  latestRuntimeState: Record<string, unknown> | null;
}

export interface ProcessRunsResponse {
  items: ProcessRunListItem[];
  executions: ProcessExecutionListItem[];
}

export interface ProcessRunEntryListItem {
  id: string;
  processRunId: number;
  sequence: number;
  kind: string;
  status: ProvenanceEntryStatus;
  detail: Record<string, unknown> | null;
  error: string | null;
  createdAt: string;
}

export interface ProcessRunEntriesResponse {
  items: ProcessRunEntryListItem[];
}

export interface ProcessAgentOption {
  id: number;
  name: string;
  description: string | null;
  ownerScope: OwnerScope;
  lastChange?: ConfigChangeSummary | null;
}

export interface ProcessAgentOptionsResponse {
  items: ProcessAgentOption[];
}

export interface ProcessSkillOption {
  id: number;
  name: string;
  description: string | null;
  ownerScope: OwnerScope;
  lastChange?: ConfigChangeSummary | null;
}

export interface ProcessSkillOptionsResponse {
  items: ProcessSkillOption[];
}

export type CreateProcessTrigger =
  | { type: 'TAG_APPLIED'; config: { tagId: number; contentType?: string } }
  | { type: 'MESSAGE_IMPORTED'; config: { directions?: MessageDirection[] } }
  | { type: 'EMAIL_RECEIVED'; config: { matchCriteria: string } }
  | { type: 'SCHEDULE'; config: { cron: string; timezone: string; runAsMemberId?: number } }
  | { type: 'XERO_EVENT'; config: XeroEventTriggerDefinition };

export interface CreateProcessInput {
  name: string;
  description?: string;
  agentId: number;
  skillIds?: number[];
  inputTemplate?: string;
  enabled?: boolean;
  trigger: CreateProcessTrigger;
}

export async function listProcesses(): Promise<ProcessesResponse> {
  return httpClient.get<ProcessesResponse>('/protected/processes');
}

export async function listProcessAgentOptions(): Promise<ProcessAgentOptionsResponse> {
  return httpClient.get<ProcessAgentOptionsResponse>('/protected/processes/agent-options');
}

export async function listProcessSkillOptions(): Promise<ProcessSkillOptionsResponse> {
  return httpClient.get<ProcessSkillOptionsResponse>('/protected/processes/skill-options');
}

export async function createProcess(input: CreateProcessInput): Promise<ProcessListItem> {
  return httpClient.post<ProcessListItem, unknown>('/protected/processes', {
    name: input.name,
    description: input.description,
    agentId: input.agentId,
    ...(input.skillIds?.length ? { skillIds: input.skillIds } : {}),
    inputTemplate: input.inputTemplate,
    trigger: input.trigger,
    enabled: input.enabled ?? true,
  });
}

export async function updateProcessEnabled(processId: string, enabled: boolean): Promise<ProcessListItem> {
  return httpClient.patch<ProcessListItem, { enabled: boolean }>(`/protected/processes/${processId}`, { enabled });
}

export type UpdateProcessTrigger =
  | { type: 'TAG_APPLIED'; config: { tagId: number; contentType?: string } }
  | { type: 'MESSAGE_IMPORTED'; config: { directions?: MessageDirection[] } }
  | { type: 'EMAIL_RECEIVED'; config: { matchCriteria: string } }
  | { type: 'SCHEDULE'; config: { cron: string; timezone: string; runAsMemberId?: number } }
  | { type: 'XERO_EVENT'; config: XeroEventTriggerDefinition };

export interface UpdateProcessInput {
  name?: string;
  description?: string;
  agentId?: number;
  skillIds?: number[];
  inputTemplate?: string;
  enabled?: boolean;
  trigger?: UpdateProcessTrigger;
}

export async function updateProcess(processId: string, input: UpdateProcessInput): Promise<ProcessListItem> {
  return httpClient.patch<ProcessListItem, UpdateProcessInput>(`/protected/processes/${processId}`, input);
}

export async function deleteProcess(processId: string): Promise<void> {
  return httpClient.delete<void>(`/protected/processes/${processId}`);
}

export interface ProcessRunStatusSnapshot {
  status: ProcessRunStatus;
  error: string | null;
}

export async function getProcessRunById(runId: number): Promise<ProcessRunStatusSnapshot> {
  return httpClient.get<ProcessRunStatusSnapshot>(`/protected/process-runs/${runId}`);
}

export async function listActiveProcessRuns(): Promise<{ items: ActiveProcessRunItem[] }> {
  return httpClient.get<{ items: ActiveProcessRunItem[] }>('/protected/process-runs');
}

export async function cancelProcessRun(runId: string | number, kind: ProcessRunRefKind): Promise<MissionControlCancelRunResponse> {
  return httpClient.post<MissionControlCancelRunResponse, { kind: ProcessRunRefKind }>(
    `/protected/process-runs/${runId}/cancel`,
    { kind }
  );
}

export async function listProcessRuns(processId: string, limit = 20): Promise<ProcessRunsResponse> {
  return httpClient.get<ProcessRunsResponse>(`/protected/processes/${processId}/runs?limit=${limit}`);
}

export async function listProcessRunEntries(processId: string, runId: number): Promise<ProcessRunEntriesResponse> {
  return httpClient.get<ProcessRunEntriesResponse>(`/protected/processes/${processId}/runs/${runId}/entries`);
}
